import { HttpErrorResponse } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { ComponentStore } from '@ngrx/component-store'
import { tapResponse } from '@ngrx/operators'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { ItemsApiService } from 'libs/gb-dashboard/item/data-access/src/lib/+state/items-api.service'
import { SlimItemsEntity, SlimItemsResponse } from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.models'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { debounceTime, switchMap, tap, withLatestFrom } from 'rxjs/operators'

interface State {
  results: SlimItemsEntity[]
  loaded: boolean
  offset: number
  limit: number
  count: number
  search?: string
  error?: HttpErrorResponse
}

@Injectable()
export class GbEditCategoryStore extends ComponentStore<State> {
  readonly loaded$ = this.select((state) => state.loaded)
  readonly count$ = this.select((state) => state.count)
  readonly limit$ = this.select((state) => state.limit)
  readonly items$ = this.select((state) => state.results)

  constructor(
    @Inject(APP_CONFIG) private appConfig: EnvironmentModel,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private itemsApiService: ItemsApiService,
  ) {
    super({ results: [], loaded: true, offset: 0, limit: 0, count: 0, search: '' })
  }

  readonly loadFirstPageItems = this.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.getItems({ offset: 0, limit: this.appConfig.itemsPageSize }))),
  )

  readonly loadNextPageItems = this.effect<{ offset: number; limit: number }>((params$) =>
    params$.pipe(
      withLatestFrom(this.state$),
      tap(([{ limit, offset }, state]) => this.getItems({ offset, limit, search: state.search })),
    ),
  )

  readonly searchItems = this.effect<string>((search$) =>
    search$.pipe(
      tap(() => this.patchState({ loaded: false })),
      debounceTime(500),
      withLatestFrom(this.state$),
      tap(([search, state]) => this.getItems({ offset: 0, limit: state.limit, search })),
    ),
  )

  private getItems = this.effect<{ offset: number; limit: number; search?: string }>((params$) =>
    params$.pipe(
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ offset, limit, search }, urlName]) => {
        this.patchState({ loaded: false, offset, limit, search })
        return this.itemsApiService.getItems(urlName, offset, { search }, limit).pipe(
          tapResponse({
            next: (response: SlimItemsResponse) =>
              this.patchState({ results: response.results, count: response.count, loaded: true, offset }),
            error: (error: HttpErrorResponse) => this.patchState({ error, loaded: true, offset }),
          }),
        )
      }),
    ),
  )
}
